import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEO } from '../components/seo/SEO';
import { useAuth } from '../context/AuthContext';
import { getTriggerLabel, getExperienceLabel } from '../data/synesthesiaIdentifierOptions';
import { getTypeIdForPair, getTypeContent } from '../data/synesthesiaIdentifierResults';
import './SynesthesiaIdentifierLayout.css';
import './SynesthesiaIdentifierTriggerPage.css';
import './SynesthesiaIdentifierResultPage.css';

const SITE_URL = 'https://synesthetic.ca';

// Inline styles so the two buttons always show (no dependency on CSS load order or overrides)
const resultButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 20px',
  fontSize: '15px',
  fontWeight: 600,
  textDecoration: 'none',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
  background: '#f3f4f6',
  color: '#111827'
};

export const SynesthesiaIdentifierResultPage = () => {
  const { triggerSlug, experienceSlug } = useParams();
  const { user } = useAuth();
  const triggerLabel = getTriggerLabel(triggerSlug);
  const experienceLabel = getExperienceLabel(experienceSlug);

  const typeId = getTypeIdForPair(triggerSlug, experienceSlug);
  const type = typeId ? getTypeContent(typeId) : null;
  const isGraphemeColour = typeId === 'grapheme-colour';
  const profilePath = user ? '/color-profile' : '/signup';
  const visualizerPath = user ? '/visualize' : '/#visualizer';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Self Diagnose Synesthesia', item: `${SITE_URL}/synesthesia-self-diagnose` },
      { '@type': 'ListItem', position: 2, name: 'Start', item: `${SITE_URL}/synesthesia-self-diagnose/start` },
      { '@type': 'ListItem', position: 3, name: 'Your result', item: `${SITE_URL}/synesthesia-self-diagnose/result/${triggerSlug}/${experienceSlug}` }
    ]
  };

  const seoTitle = type?.metaTitle || `${triggerLabel} + ${experienceLabel} | Self Diagnose Synesthesia Result`;
  const seoDescription = type?.metaDescription || `Your result: ${triggerLabel} and ${experienceLabel}. We suggest your synesthesia type and link you to resources. Find out more.`;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={`/synesthesia-self-diagnose/result/${triggerSlug}/${experienceSlug}`}
        canonical={`${SITE_URL}/synesthesia-self-diagnose/result/${triggerSlug}/${experienceSlug}`}
        schema={breadcrumbSchema}
      />
      <div className="identifier-trigger-page identifier-result-page">
        <div className="identifier-trigger-container identifier-result-container identifier-content-wrap">
          <Link to="/synesthesia-self-diagnose/start" className="identifier-trigger-back">
            <span className="identifier-trigger-back-arrow" aria-hidden>←</span>
            Start over
          </Link>

          {type ? (
            <>
              <section className="identifier-result-hero" aria-labelledby="result-headline">
                <h1 id="result-headline" className="identifier-result-headline">
                  {type.headline.includes(type.name) ? (
                    <>
                      {type.headline.split(type.name)[0]}
                      <strong>{type.name}</strong>
                      {type.headline.split(type.name)[1]}
                    </>
                  ) : (
                    type.headline
                  )}
                </h1>
              </section>

              <p className="identifier-result-lead">{type.lead}</p>

              <div className="identifier-result-blurb">
                <p>{type.blurb}</p>
              </div>

              {type.exampleSnippet && (
                <div className="identifier-result-example" aria-label="What it might feel like">
                  <p className="identifier-result-example-label">What it might feel like</p>
                  <p className="identifier-result-example-text">{type.exampleSnippet}</p>
                </div>
              )}

              <div className="identifier-result-actions">
                {isGraphemeColour ? (
                  <>
                    <Link to={profilePath} className="identifier-result-cta">
                      Build your color profile to see it in action
                    </Link>
                    {type.readMoreUrl && (
                      <a
                        href={type.readMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="identifier-result-link"
                      >
                        Read more about {type.name}
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    {type.readMoreUrl && (
                      <a
                        href={type.readMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="identifier-result-cta"
                      >
                        Read more about {type.name}
                      </a>
                    )}
                    <Link to={visualizerPath} className="identifier-result-link">
                      Or try the visualizer anyway
                    </Link>
                  </>
                )}
              </div>

              <div className="identifier-result-next" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '28px' }}>
                <Link to="/synesthesia-self-diagnose" className="identifier-result-next-btn" style={resultButtonStyle}>Try another combination</Link>
                <Link to="/synesthesia-self-diagnose/start" className="identifier-result-next-btn" style={resultButtonStyle}>Start over</Link>
              </div>
            </>
          ) : (
            <>
              <section className="identifier-trigger-hero">
                <h1 className="identifier-trigger-title">Your result</h1>
                <p className="identifier-trigger-subtitle">
                  You chose <strong>{triggerLabel}</strong> and <strong>{experienceLabel}</strong>. We are still adding content for this combination.
                </p>
              </section>
              <p className="identifier-result-blurb">
                Check back later or explore our{' '}
                <a href="https://synesthetic.ca/synesthesia-glossary" target="_blank" rel="noopener noreferrer">
                  synesthesia glossary
                </a>{' '}
                to learn about other types.
              </p>
              <div className="identifier-result-actions">
                <a
                  href="https://synesthetic.ca/synesthesia-glossary"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="identifier-result-cta"
                >
                  Explore the glossary
                </a>
                <Link to={visualizerPath} className="identifier-result-link">
                  Or try the visualizer anyway
                </Link>
              </div>
              <div className="identifier-result-next" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '28px' }}>
                <Link to="/synesthesia-self-diagnose" className="identifier-result-next-btn" style={resultButtonStyle}>Try another combination</Link>
                <Link to="/synesthesia-self-diagnose/start" className="identifier-result-next-btn" style={resultButtonStyle}>Start over</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
